import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import './CreateJournalEntry.css';

function CreateJournalEntry() {
  const [formData, setFormData] = useState({
    entryDate: new Date().toISOString().split('T')[0],
    description: '',
    reference: ''
  });
  
  const [lines, setLines] = useState([
    { accountId: '', debit: 0, credit: 0, description: '' },
    { accountId: '', debit: 0, credit: 0, description: '' }
  ]);
  
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const statusResponse = await axios.get('/auth/status');
      const organizations = statusResponse.data.organizations || [];
      
      if (organizations.length === 0) return;
      
      const organizationId = organizations[0].realmId;
      const response = await axios.get(`/api/accounting/accounts?organizationId=${organizationId}`);
      setAccounts(response.data.data || []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...lines];
    newLines[index][field] = value;
    setLines(newLines);
  };

  const addLine = () => {
    setLines([...lines, { accountId: '', debit: 0, credit: 0, description: '' }]);
  };

  const removeLine = (index) => {
    if (lines.length > 2) {
      const newLines = lines.filter((_, i) => i !== index);
      setLines(newLines);
    }
  };

  const calculateTotals = () => {
    const totalDebits = lines.reduce((sum, line) => sum + parseFloat(line.debit || 0), 0);
    const totalCredits = lines.reduce((sum, line) => sum + parseFloat(line.credit || 0), 0);
    return { totalDebits, totalCredits };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const { totalDebits, totalCredits } = calculateTotals();
    
    if (totalDebits !== totalCredits) {
      setError(`Journal entry is not balanced! Debits: $${totalDebits.toFixed(2)}, Credits: $${totalCredits.toFixed(2)}`);
      return;
    }

    if (totalDebits === 0 || totalCredits === 0) {
      setError('Journal entry must have at least one debit and one credit');
      return;
    }

    setLoading(true);

    try {
      const statusResponse = await axios.get('/auth/status');
      const organizations = statusResponse.data.organizations || [];
      
      if (organizations.length === 0) {
        setError('No QuickBooks organization found');
        setLoading(false);
        return;
      }

      const organizationId = organizations[0].realmId;

      const payload = {
        organizationId,
        entryDate: formData.entryDate,
        description: formData.description,
        reference: formData.reference,
        lines: lines.map(line => ({
          accountId: line.accountId,
          debit: parseFloat(line.debit || 0),
          credit: parseFloat(line.credit || 0),
          description: line.description
        }))
      };

      const response = await axios.post('/api/accounting/journal-entries', payload);
      
      setSuccess('Journal entry created successfully!');
      
      // Reset form
      setFormData({
        entryDate: new Date().toISOString().split('T')[0],
        description: '',
        reference: ''
      });
      setLines([
        { accountId: '', debit: 0, credit: 0, description: '' },
        { accountId: '', debit: 0, credit: 0, description: '' }
      ]);

      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error creating journal entry:', err);
      setError(err.response?.data?.error || 'Failed to create journal entry');
    } finally {
      setLoading(false);
    }
  };

  const { totalDebits, totalCredits } = calculateTotals();
  const isBalanced = totalDebits === totalCredits && totalDebits > 0;

  return (
    <div className="create-journal-entry-container">
      <h2>📝 Create Journal Entry</h2>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          <span className="success-icon">✅</span>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="journal-entry-form">
        <div className="form-header">
          <div className="form-group">
            <label>Entry Date *</label>
            <input
              type="date"
              name="entryDate"
              value={formData.entryDate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Reference</label>
            <input
              type="text"
              name="reference"
              value={formData.reference}
              onChange={handleChange}
              placeholder="e.g., INV-001"
            />
          </div>
        </div>

        <div className="form-group full-width">
          <label>Description *</label>
          <input
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="e.g., Initial capital investment"
            required
          />
        </div>

        <div className="lines-section">
          <div className="lines-header">
            <h3>Journal Entry Lines</h3>
            <button type="button" onClick={addLine} className="btn btn-secondary btn-sm">
              + Add Line
            </button>
          </div>

          <div className="lines-table">
            <div className="table-header">
              <div className="col-account">Account</div>
              <div className="col-description">Description</div>
              <div className="col-amount">Debit</div>
              <div className="col-amount">Credit</div>
              <div className="col-action">Action</div>
            </div>

            {lines.map((line, index) => (
              <div key={index} className="table-row">
                <div className="col-account">
                  <select
                    value={line.accountId}
                    onChange={(e) => handleLineChange(index, 'accountId', e.target.value)}
                    required
                  >
                    <option value="">Select Account</option>
                    {accounts.map((account) => (
                      <option key={account.Id} value={account.Id}>
                        {account.Name} ({account.AccountType})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-description">
                  <input
                    type="text"
                    value={line.description}
                    onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                    placeholder="Line description"
                  />
                </div>

                <div className="col-amount">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={line.debit}
                    onChange={(e) => handleLineChange(index, 'debit', e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div className="col-amount">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={line.credit}
                    onChange={(e) => handleLineChange(index, 'credit', e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div className="col-action">
                  <button
                    type="button"
                    onClick={() => removeLine(index)}
                    className="btn btn-danger btn-sm"
                    disabled={lines.length <= 2}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="totals-row">
            <div className="totals-label">Totals:</div>
            <div className="totals-values">
              <div className={`total-debit ${!isBalanced && totalDebits > 0 ? 'unbalanced' : ''}`}>
                Debit: ${totalDebits.toFixed(2)}
              </div>
              <div className={`total-credit ${!isBalanced && totalCredits > 0 ? 'unbalanced' : ''}`}>
                Credit: ${totalCredits.toFixed(2)}
              </div>
              <div className={`balance-status ${isBalanced ? 'balanced' : 'unbalanced'}`}>
                {isBalanced ? '✅ Balanced' : '⚠️ Not Balanced'}
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading || !isBalanced}>
            {loading ? 'Creating...' : 'Create Journal Entry'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateJournalEntry;
